import { Button, InputCurrency } from "@sellar-io/components";
import type {
  PriceListListResponseType,
  SupplierProductGetResponseChildType,
} from "@sellar-io/api-v2/types";
import { useState } from "react";
import { twMerge } from "tailwind-merge";
import { Icon } from "../../../../../../../components";
import { InputContainer } from "@sellar-io/components";
import {
  Dialog,
  DialogNavigation,
} from "../../../../../../../components/modal/Dialog";
import { useProductPriceListCreate } from "../../../../../../../hooks/api/v2/productPriceList/useProductPriceListCreate";
import { useProductPriceListDelete } from "../../../../../../../hooks/api/v2/productPriceList/useProductPriceListDelete";
import { useProductPriceListUpdate } from "../../../../../../../hooks/api/v2/productPriceList/useProductPriceListUpdate";
import { usePriceFormat } from "../../../../../../../hooks/usePriceFormat";

export const ProductCategoryItemPriceRow = ({
  priceList,
  product,
}: {
  priceList: PriceListListResponseType[number];
  product: SupplierProductGetResponseChildType;
}) => {
  const productPriceList = priceList.ProductPriceLists[0];
  const serverPrice = productPriceList?.price;
  const serverDiscountable = productPriceList?.discountable ?? true;
  const serverActive = productPriceList
    ? (priceList.ProductAvailabilityTiers[0]?.active ?? true)
    : false;

  const [initialPrice, setInitialPrice] = useState<number | undefined>(
    serverPrice,
  );

  const priceFormat = usePriceFormat(serverPrice);

  const [initialDiscountable, setInitialDiscountable] =
    useState(serverDiscountable);
  const [discountable, setDiscountable] = useState(initialDiscountable);

  const [initialActive, setInitialActive] = useState(serverActive);
  const [active, setActive] = useState(initialActive);

  const productPriceListUpdate = useProductPriceListUpdate(
    productPriceList?.id ?? 0,
  );
  const productPriceListCreate = useProductPriceListCreate();
  const productPriceListDelete = useProductPriceListDelete(
    productPriceList?.id ?? 0,
  );

  const [needPriceModalActive, setNeedPriceModalActive] = useState(false);

  const loading =
    productPriceListCreate.isPending ||
    productPriceListUpdate.isPending ||
    productPriceListDelete.isPending;

  if (
    serverPrice !== initialPrice ||
    serverDiscountable !== initialDiscountable ||
    serverActive !== initialActive
  ) {
    setInitialPrice(serverPrice);
    setInitialDiscountable(serverDiscountable);
    setDiscountable(serverDiscountable);
    setInitialActive(serverActive);
    setActive(serverActive);
  }

  const hasChangedValues =
    initialPrice !== priceFormat.apiFormat ||
    discountable !== initialDiscountable ||
    active !== initialActive;

  return (
    <tr className={twMerge(hasChangedValues ? "bg-slate-100" : "bg-white")}>
      <td
        className={twMerge(
          "sticky left-0 z-10 border-r border-solid px-4 py-1",
          hasChangedValues ? "bg-slate-100" : "bg-white",
        )}
      >
        <div className="flex items-center gap-2">{priceList.name}</div>
      </td>
      <td className={"px-4 py-1"}>
        <input
          type={"checkbox"}
          checked={discountable}
          onChange={() => setDiscountable(!discountable)}
          disabled={!active}
        />
      </td>
      <td className={"flex items-center gap-2 px-4 py-1"}>
        <InputContainer className={"w-[16ch]"}>
          <InputCurrency
            showPrefix
            allowNegative
            placeholder="No price"
            value={priceFormat.inputFormat}
            onChange={(e) => priceFormat.setInputFormat(e.target.value)}
            min={0}
          />
        </InputContainer>
        <Button
          className={"px-3"}
          size={"small"}
          disabled={!hasChangedValues}
          loading={loading}
          onClick={() => {
            if (!priceList.ProductPriceLists[0]?.id) {
              if (priceFormat.apiFormat === null) {
                //trying to create a price tier without a price
                return setNeedPriceModalActive(true);
              }

              return productPriceListCreate.mutate({
                ProductId: product.id,
                PriceListId: priceList.id,
                basePrice: null,
                price: priceFormat.apiFormat,
                discountable,
                active: true,
              });
            }

            if (priceFormat.apiFormat === null) {
              //if price not defined and price tier exists, delete price tier
              return productPriceListDelete.mutate();
            }

            return productPriceListUpdate.mutate(
              {
                price: priceFormat.apiFormat,
                discountable,
                active,
              },
              {
                onSuccess: (data) => setInitialPrice(data.price),
              },
            );
          }}
        >
          Update
        </Button>
        <Button
          className={twMerge(!hasChangedValues && "invisible")}
          size={"small"}
          color={"transparent"}
          onClick={() => {
            setDiscountable(initialDiscountable);
            priceFormat.setApiFormat(initialPrice ?? null);
            setActive(initialActive);
          }}
        >
          <Icon icon={"UNDO"} />
        </Button>
        <Dialog
          active={needPriceModalActive}
          onDismiss={() => setNeedPriceModalActive(false)}
        >
          <DialogNavigation
            leftIcon={"CHEVRON_DOWN"}
            onClickLeftIcon={() => setNeedPriceModalActive(false)}
            title={"Add a price"}
            description={`You need to add a price to show this product on the ${priceList.name} price tier.`}
          ></DialogNavigation>
        </Dialog>
      </td>
    </tr>
  );
};
